// src/pages/CourseTime/components/ActivityResponseDisplay.comp.jsx
import React from 'react';
import {
  ActivityResponseItem,
  ResponseHeader,
  ResponseUserInfo,
  ResponseUserAvatar,
  ResponseUserName,
  ResponseTime,
  ResponseContent,
  TextResponseContent
} from '../styles/activity.styles';
import PollResultsChart from './PollResultsChart.comp';
import { GET_APP_LOCALIZE_TEXT } from '../../../utils/dq_lc_service/lc_service';

// Text Response Component (for Journaling and Share Work)
const TextResponse = ({ response, formatTimestamp, themeColor }) => (
  <ActivityResponseItem themeColor={themeColor}>
    <ResponseHeader>
      <ResponseUserInfo>
        <ResponseUserAvatar themeColor={themeColor}>
          {response.user_initials}
        </ResponseUserAvatar>
        <ResponseUserName>{response.user_name}</ResponseUserName>
      </ResponseUserInfo>
      <ResponseTime>{formatTimestamp(response.interaction_data.timestamp)}</ResponseTime>
    </ResponseHeader>
    <ResponseContent>
      <TextResponseContent themeColor={themeColor}>
        {response.interaction_data.text}
      </TextResponseContent>
    </ResponseContent>
  </ActivityResponseItem>
);

// Main Activity Response Display Component
const ActivityResponseDisplay = ({ 
  activityType, 
  responses, 
  pollResults, 
  formatTimestamp,
  themeColor = '#e91e63', // Default fallback color
  currentActivity = null, // Optional: current activity data for poll type detection
  pageLc
}) => {
  // Transform poll results data to match PollResultsChart expected format
  const transformPollResults = (pollResults) => {
    if (!pollResults || !pollResults.options) {
      return null;
    }

    // Determine poll type: first try from activity, then from responses
    let pollType = 'MCQ'; // default
    
    // Check activity's interaction_data.category11 if available
    if (currentActivity?.interaction_data?.category11) {
      pollType = currentActivity.interaction_data.category11;
    } else {
      // Fallback: check if any response has multiple selections
      const hasMultipleSelections = responses?.some(
        response => Array.isArray(response.interaction_data?.selectedOptions)
      );
      pollType = hasMultipleSelections ? 'MAQ' : 'MCQ';
    }

    // Handle different API response formats
    // Case 1: options is array of strings, results is separate array
    // Case 2: options is array of objects with {text, count, percentage}
    let transformedOptions = [];
    
    if (pollResults.results && Array.isArray(pollResults.results)) {
      // Case 1: options are strings, results are separate
      const statsMap = {};
      pollResults.results.forEach(result => {
        statsMap[result.index] = {
          count: result.count,
          percentage: result.percentage
        };
      });

      transformedOptions = pollResults.options.map((option, index) => {
        // Handle both string and object formats
        const optionText = typeof option === 'string' ? option : option.text;
        return {
          text: optionText,
          count: statsMap[index]?.count || (typeof option === 'object' ? option.count : 0),
          percentage: statsMap[index]?.percentage || (typeof option === 'object' ? option.percentage : 0)
        };
      });
    } else {
      // Case 2: options already contain the stats as objects
      transformedOptions = pollResults.options.map((option) => {
        if (typeof option === 'string') {
          // If it's a string, return with default values
          return {
            text: option,
            count: 0,
            percentage: 0
          };
        } else {
          // If it's an object, extract the values
          return {
            text: option.text || '',
            count: option.count || 0,
            percentage: option.percentage || 0
          };
        }
      });
    }

    return {
      question: pollResults.question || GET_APP_LOCALIZE_TEXT(pageLc, 'ct__poll_question'),
      totalResponses: pollResults.totalResponses || transformedOptions.reduce((sum, opt) => sum + (opt.count || 0), 0),
      pollType: pollType,
      options: transformedOptions
    };
  };

  const renderResponse = (response, index) => {
    switch (activityType) {
      case 'JOURNALING_SELF_REFLECTION_ACTIVITY':
      case 'SHARE_WORK_FILE_ACTIVITY':
        return (
          <TextResponse 
            key={index} 
            response={response} 
            formatTimestamp={formatTimestamp}
            themeColor={themeColor}
          />
        );
      
      // Poll activities don't show individual responses - only aggregated chart
      case 'POLL_QUIZ_ACTIVITY':
        return null;
      
      default:
        return null;
    }
  };

  // For poll activities, only show the chart, no individual responses
  if (activityType === 'POLL_QUIZ_ACTIVITY') {
    const transformedPollData = transformPollResults(pollResults);
    
    if (!transformedPollData) {
      return null;
    }

    return (
      <div style={{ padding: '0' }}>
        <PollResultsChart pollData={transformedPollData} pageLc={pageLc} />
      </div>
    );
  }

  // For text activities, show individual responses
  return (
    <>
      {responses?.map((response, index) => renderResponse(response, index))}
    </>
  );
};

export default ActivityResponseDisplay;
